import {
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@material-ui/core";
import React, { FC, useCallback } from "react";
import styled from "styled-components";
import { Logout } from "../../../icons/Logout";
import { Icon } from "../../../icons/components/Icon";
import { HeaderAvatar } from "./HeaderAvatar";
import { useRouter } from "next/router";
import { useSelector } from "react-redux";
import { getAuthState } from "../../auth";
import useTranslateMessage from "../../../hooks/useTranslateMessage";
import { UserRole } from "../../../generated/graphql";

const roleHebrewMap: Partial<Record<UserRole, string>> = {
  [UserRole.OrderModerator]: "מנהל הזמנות",
  [UserRole.OrderCreator]: "יוצר הזמנות",
  [UserRole.CatalogManager]: "מנהל קטלוג",
  [UserRole.SystemAdmin]: "מנהל מערכת",
  [UserRole.OrderModeratorLimited]: "מנהל הזמנות (מוגבל)",
  [UserRole.ClientUser]: "לקוח",
};

const SettingsIcon: FC = () => (
  <Icon d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.8,11.69,4.8,12s0.02,0.64,0.07,0.94l-2.03,1.58c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z" />
);

const StyledHeaderUserMenu = styled.div`
  line-height: 0;
`;

const UserInfoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
`;

const UserInfoAvatar = styled.div<{ $dark?: boolean }>`
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 50%;
  background: ${({ theme, $dark }) =>
    $dark ? "#111111" : theme.colors.yellow};
  color: ${({ theme, $dark }) => ($dark ? theme.colors.yellow : "inherit")};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 16px;
  text-transform: uppercase;
`;

const UserInfoText = styled.div`
  flex: 1;
  text-align: right;
`;

const UserInfoName = styled.div`
  font-weight: 700;
  font-size: 14px;
`;

const UserInfoSubtitle = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey};
`;

const CompanySettingsMenuItem = styled(MenuItem)`
  &.MuiMenuItem-root {
    background-color: ${({ theme }) => theme.colors.yellow}33;
    &:hover {
      background-color: ${({ theme }) => theme.colors.yellow}66;
    }
  }
` as typeof MenuItem;

const StyledListItemIcon = styled(ListItemIcon)`
  &.MuiListItemIcon-root {
    min-width: 40px;
  }
`;

interface Props {
  className?: string;
  userName?: string;
  userImage?: string;
  avatarVariant?: "light" | "dark";
  onRequestLogout: () => void;
}

const HeaderUserMenu: FC<Props> = ({
  className,
  userName,
  userImage,
  avatarVariant,
  onRequestLogout,
}) => {
  const t = useTranslateMessage();
  const authState = useSelector(getAuthState());
  const router = useRouter();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const { roles, companyName } = authState;
  const primaryRole = roles?.[0];
  const roleLabel = primaryRole ? roleHebrewMap[primaryRole] : undefined;
  const subtitle = [roleLabel, companyName].filter(Boolean).join(" - ");

  const initials = userName
    ? userName
        .split(" ")
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
    : "";

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleRequestLogout = useCallback(() => {
    handleCloseMenu();
    onRequestLogout();
  }, [onRequestLogout]);

  const handleCompanySettings = () => {
    handleCloseMenu();
    router.push("/profile");
  };

  return (
    <StyledHeaderUserMenu className={className}>
      <HeaderAvatar
        name={userName}
        image={userImage}
        variant={avatarVariant}
        onClick={handleOpenMenu}
        data-cy="header-avatar"
      />

      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        {/* User info — not a MenuItem so it's non-interactive */}
        <UserInfoWrapper>
          {/* In RTL flex: first child = right, second = left */}
          <UserInfoAvatar $dark={avatarVariant === "dark"}>
            {initials}
          </UserInfoAvatar>
          <UserInfoText>
            <UserInfoName>{userName}</UserInfoName>
            <UserInfoSubtitle>{subtitle}</UserInfoSubtitle>
          </UserInfoText>
        </UserInfoWrapper>

        <Divider />

        {/* In RTL flex: ListItemText (first) = right, StyledListItemIcon (second) = left */}
        <CompanySettingsMenuItem
          onClick={handleCompanySettings}
          data-cy="header-company-settings"
        >
          <ListItemText
            primary={t({ id: "HeaderNavigation-companySettings" })}
            secondary={t({ id: "headerUserMenu-companySettingsSubtitle" })}
          />
          <StyledListItemIcon>
            <SettingsIcon />
          </StyledListItemIcon>
        </CompanySettingsMenuItem>

        <MenuItem onClick={handleRequestLogout} data-cy="header-logout">
          <ListItemText
            primary={t({ id: "headerUserMenu-logoutButton" })}
            primaryTypographyProps={{ style: { color: "#FA1515" } }}
          />
          <StyledListItemIcon>
            <Logout color="#FA1515" />
          </StyledListItemIcon>
        </MenuItem>
      </Menu>
    </StyledHeaderUserMenu>
  );
};

export { HeaderUserMenu };
